import { useFields } from '@hooks'
import { useEffect, useMemo } from 'react'
import { Column, SortingRule, useFlexLayout, useSortBy, useTable } from 'react-table'
import {
  Button,
  ButtonGroup,
  Card,
  Flexbox,
  Icon,
  Modal,
  Search,
  Table,
  Text,
} from '@components/common'
import { MaintenanceContract } from '@api/models'
import Pagination from '@components/Pagination'
import { MaintenancesHook } from '../useMaintenances'
import { MAINTENANCE_FORM_ID } from '@constants'
import MaintenanceForm from '../MaintenanceForm'
import MaintenanceContractLoading from './MaintenancesTableLoading'

const MaintenancesTable = ({
  pageNumber,
  itemsPerPage,
  handleSearch,
  searchValue,
  changePage,
  sortByInitialState,
  updateSortSearchParam,
  //
  searchKey,
  maintenances,
  isLoadingMaintenances,
  isErrorFetchingMaintenances,
  isRefetchingMaintenances,

  handleDeleteMaintenance,
  isLoadingDeleteMaintenance,
  openDeleteMaintenanceModal,
  selectedMaintenance,
  deleteMaintenanceModalIsOpen,
  closeDeleteMaintenanceModal,
  openEditMaintenanceModal,
  closeEditMaintenanceModal,
  editMaintenanceModalIsOpen,
  handleEditMaintenance,
  isLoadingEditMaintenance,
  canEdit,
}: MaintenancesHook) => {
  // Declare columns
  const { fields, isLoading: isLoadingFields, isError: isErrorGettingFields } = useFields()

  const columns = useMemo<Column<MaintenanceContract>[]>(
    () => [
      {
        Header: fields.MN_CON_ID,
        accessor: 'MN_CON_ID',
      },
      {
        Header: fields.MN_DESC,
        accessor: 'MN_DESC',
      },
      {
        Header: fields.MN_NAME,
        accessor: 'MN_NAME',
      },
      {
        Header: '',
        accessor: 'MN_CON_FX',
        disableSortBy: true,
        Cell: ({ row }) => (
          <Flexbox alignItems="center" justifyContent="flex-end" style={{ width: '100%' }}>
            <ButtonGroup hidden={!canEdit}>
              <Button
                icon={<Icon name="Edit" />}
                size="compact"
                onClick={e => {
                  e.stopPropagation()
                  openEditMaintenanceModal(row.original)
                }}
              >
                Edit
              </Button>

              <Button
                icon={<Icon name="Trash2" />}
                size="compact"
                intent="danger"
                onClick={e => {
                  e.stopPropagation()
                  openDeleteMaintenanceModal(row.original)
                }}
              >
                Delete
              </Button>
            </ButtonGroup>
          </Flexbox>
        ),
      },
    ],
    [isLoadingFields]
  )

  // Declare react table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setSortBy,
    state: { sortBy },
  } = useTable(
    {
      columns,
      data: maintenances ? maintenances.list : [],
      manualSortBy: true,
      disableMultiSort: true,
      initialState: {
        sortBy: sortByInitialState() as SortingRule<MaintenanceContract>[],
      },
    },
    useSortBy,
    useFlexLayout
  )

  // Update sort param
  useEffect(() => {
    updateSortSearchParam(sortBy, setSortBy)
  }, [sortBy])

  // Returns
  const isTableLoading = isLoadingMaintenances || isLoadingFields
  const isError = isErrorFetchingMaintenances || isErrorGettingFields

  if (isTableLoading) {
    return <MaintenanceContractLoading />
  }

  if (isError) {
    return <Table.NoResults heading="There was an error." />
  }

  return (
    <>
      <Modal
        isOpen={deleteMaintenanceModalIsOpen}
        close={closeDeleteMaintenanceModal}
        title={`Delete maintenance contract ${selectedMaintenance?.MN_CON_ID} (${selectedMaintenance?.MN_DESC})`}
        width={540}
        action={() => handleDeleteMaintenance(selectedMaintenance)}
        actionLabel={`Delete maintenance contract`}
        intent="danger"
        actionAutoFocus
        isActionLoading={isLoadingDeleteMaintenance}
      >
        <Text p>
          Are you sure you want to delete maintenance contract{' '}
          <strong>
            {selectedMaintenance?.MN_CON_ID} ({selectedMaintenance?.MN_DESC})
          </strong>
          ? <Text intent="danger">This cannot be undone</Text>.
        </Text>
      </Modal>

      <Modal
        isOpen={editMaintenanceModalIsOpen}
        close={closeEditMaintenanceModal}
        title={`Edit maintenance contract ${selectedMaintenance?.MN_CON_ID} (${selectedMaintenance?.MN_DESC})`}
        width={576}
        actionLabel="Save changes"
        form={MAINTENANCE_FORM_ID}
        isActionLoading={isLoadingEditMaintenance}
        dismissLabel={canEdit ? 'Discard Changes' : 'Close'}
        actionDisabled={!canEdit}
      >
        <MaintenanceForm
          isEdit={true}
          initialValues={selectedMaintenance}
          onSubmit={handleEditMaintenance}
          canEdit={canEdit}
        />
      </Modal>

      <Card spacing="none">
        <Table.Actions>
          <Flexbox>
            <Search
              onChange={handleSearch}
              placeholder="Search by contract description..."
              name={searchKey}
              width={'640px'}
              value={searchValue}
            />
          </Flexbox>
        </Table.Actions>
        {maintenances.list.length === 0 ? (
          <Table.NoResults text="" />
        ) : (
          <>
            <Table
              {...getTableProps()}
              aria-label="Company Table"
              label="Company Table"
              aria-colcount={columns.length}
              aria-rowcount={maintenances.list.length}
              height="calc(100vh - 280px)"
            >
              <Table.Head sticky>
                {isRefetchingMaintenances ? (
                  <Table.LoadingRows />
                ) : (
                  headerGroups.map((headerGroup, i) => (
                    <Table.HeadRow {...headerGroup.getHeaderGroupProps()} key={i}>
                      {headerGroup.headers.map((column, i) => (
                        <Table.Header
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          column={column}
                          key={i}
                          tabIndex={0}
                        >
                          {column.render('Header')}
                        </Table.Header>
                      ))}
                    </Table.HeadRow>
                  ))
                )}
              </Table.Head>

              <Table.Body {...getTableBodyProps()}>
                {rows.map((row, i) => {
                  prepareRow(row)
                  return (
                    <Table.Row
                      {...row.getRowProps()}
                      onClick={() => {
                        openEditMaintenanceModal(row.original)
                      }}
                      tabIndex={0}
                      key={i}
                    >
                      {row.cells.map(cell => (
                        <Table.Cell {...cell.getCellProps()} column={cell.column}>
                          {cell.render('Cell')}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table>
            <Pagination
              changePage={changePage}
              totalItems={maintenances.count}
              maxItemsPerPage={itemsPerPage}
              currentPageNumber={pageNumber}
              label={`Showing ${maintenances.list.length} of ${maintenances.count} record${
                maintenances.count > 1 ? 's' : ''
              }`}
            />
          </>
        )}
      </Card>
    </>
  )
}
export default MaintenancesTable
